import {
  ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import PaginatedTable from '../Common/PaginatedTable';
import { Link } from 'react-router-dom';
import { HiEye, HiXCircle } from 'react-icons/hi';
import { Warranty, WarrantyStatusStringEnum } from '../../Models/Warranty';
import { Tooltip } from 'flowbite-react';
import { formatNumbers } from '../../Utils';
import WarrantyApi from '../../Api/WarrantyApi';
import { useState } from 'react';

export default function WarrantyListTable({
  data,
  deleteWarranty,
}: {
  data: Warranty[];
  deleteWarranty(id: number): void;
}) {
  const columnHelper = createColumnHelper<Warranty>();
  async function downloadWarrantyPdf(warrantyId: number) {
    await WarrantyApi.getWarrantyReport(warrantyId)
      .then((res) => {
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Warranty_${warrantyId}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        console.error('Failed to download PDF:', err);
        alert('Failed to download PDF');
      });
  }

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([
    { id: 'statusId', value: '1' }, // default to "New"
  ]);

  const columns = [
    columnHelper.accessor((row) => row.id, {
      id: 'id',
      header: '',
      cell: (info) => (
        <div className='flex justify-center gap-1'>
          <Link
            to={`/warranty/claim/${info.getValue()}`}
            className='buttonMain p-2'
          >
            <Tooltip content={`View Warranty`}>
              <HiEye />
            </Tooltip>
          </Link>
          {/* <button
            type='button'
            className='buttonCancel p-2'
            onClick={() => deleteWarranty(info.getValue())}
          >
            <HiXCircle />
          </button> */}
        </div>
      ),
      footer: (info) => info.column.id,
      size: 35,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.id, {
      id: 'download',
      header: 'PDF',
      cell: (info) => (
        <button
          type='button'
          onClick={() => downloadWarrantyPdf(info.getValue())}
          className='text-blue-600 underline hover:text-blue-800 text-sm'
        >
          Download
        </button>
      ),
      enableSorting: false,
      enableColumnFilter: false,
      footer: (info) => info.column.id,
      size: 60,
    }),
    columnHelper.accessor((row) => row.statusId, {
      id: 'statusId',
      header: 'Status',
      cell: (info) => WarrantyStatusStringEnum[info.getValue() ?? 1],
      sortingFn: 'alphanumeric',
      filterFn: (row, columnId, filterValue) => {
        if (filterValue === undefined || filterValue === '') return true;
        const cellValue = row.getValue(columnId);
        return String(cellValue) === String(filterValue);
      },
      enableColumnFilter: true,
    }),
    columnHelper.accessor((row) => row.date, {
      id: 'date',
      header: 'Date',
      cell: (info) => {
        const value = info.getValue();
        return value ? new Date(value).toLocaleDateString() : '';
      },
      sortingFn: 'datetime',
      footer: (info) => info.column.id,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.manufacturer?.name, {
      id: 'manufacturer',
      header: 'Manufacturer',
      sortingFn: 'alphanumeric',
      footer: (info) => info.column.id,
      enableColumnFilter: true,
    }),

    columnHelper.accessor((row) => row.filledOutByName, {
      id: 'filledOutByName',
      header: 'Name',
      // cell: (info) => <i>{info.getValue()}</i>,
      sortingFn: 'alphanumeric',
      footer: (info) => info.column.id,
      filterFn: 'includesString',
      enableColumnFilter: true,
    }),
    columnHelper.accessor((row) => row.model, {
      id: 'model',
      header: 'Model',
      // cell: (info) => <i>{info.getValue()}</i>,
      sortingFn: 'alphanumeric',
      footer: (info) => info.column.id,
      filterFn: 'includesString',
      enableColumnFilter: true,
    }),
    columnHelper.accessor((row) => row.serialNumber, {
      id: 'serialNumber',
      header: 'Serial Number',
      sortingFn: 'alphanumeric',
      footer: (info) => info.column.id,
      enableColumnFilter: true,
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 20,
      },
    },
    state: {
      columnFilters,
    },
    onColumnFiltersChange: setColumnFilters,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  return (
    <>
      <div className='w-full mx-auto overflow-x-auto'>
        <PaginatedTable
          table={table}
          dataCount={data.length}
          columnResizeMode='onChange'
          showFilters={true}
        />
      </div>
    </>
  );
}
